'use client';

import React, { useEffect, useRef } from 'react';
import type { SheikhNotification } from '@/lib/hooks/useSheikhNotifications';

interface SheikhNotificationPopupProps {
  notifications: SheikhNotification[];
  onDismiss: (id: string) => void;
  onMarkAllRead: () => void;
  onViewQuestion: (questionId: string) => void;
}

function timeAgo(iso: string): string {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

// ─── Toast Popup (auto-dismiss, appears at top-right) ─────────────────────────
interface ToastProps {
  notification: SheikhNotification;
  onDismiss: (id: string) => void;
  onView: (questionId: string) => void;
}

export function SheikhNotificationToast({ notification, onDismiss, onView }: ToastProps) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    timerRef.current = setTimeout(() => {
      onDismiss(notification.id);
    }, 8000);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [notification.id, onDismiss]);

  return (
    <div className="flex items-start gap-3 bg-card border border-violet-300 rounded-2xl shadow-lg p-4 w-80 animate-slide-in-right">
      {/* Icon */}
      <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-700 rounded-xl flex items-center justify-center flex-shrink-0">
        <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-0.5">
          <p className="text-xs font-700 text-violet-700 uppercase tracking-wide">New Question Assigned</p>
          <button
            onClick={() => onDismiss(notification.id)}
            className="text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <p className="text-sm font-600 text-foreground mb-0.5 truncate">{notification.seniorName}</p>
        <p className="text-xs text-muted-foreground line-clamp-2 mb-2 leading-relaxed">
          {notification.questionType === 'audio' ?'🎙 Audio question received'
            : notification.questionText}
        </p>
        <div className="flex items-center justify-between">
          <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-500">
            {notification.specialisation}
          </span>
          <button
            onClick={() => {
              onView(notification.questionId);
              onDismiss(notification.id);
            }}
            className="text-xs font-600 text-violet-600 hover:text-violet-800 transition-colors"
          >
            View & Answer →
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Notification Bell Dropdown ───────────────────────────────────────────────
interface BellDropdownProps {
  notifications: SheikhNotification[];
  unreadCount: number;
  isOpen: boolean;
  onToggle: () => void;
  onMarkAllRead: () => void;
  onDismiss: (id: string) => void;
  onViewQuestion: (questionId: string) => void;
}

export function SheikhNotificationBell({
  notifications,
  unreadCount,
  isOpen,
  onToggle,
  onMarkAllRead,
  onDismiss,
  onViewQuestion,
}: BellDropdownProps) {
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        if (isOpen) onToggle();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onToggle]);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={onToggle}
        className="relative p-2 rounded-xl hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
        aria-label="Notifications"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-xs font-700 rounded-full flex items-center justify-center leading-none animate-pulse">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-card border border-border rounded-2xl shadow-xl z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-700 text-foreground">Notifications</h3>
              {unreadCount > 0 && (
                <span className="bg-red-100 text-red-700 text-xs font-600 px-1.5 py-0.5 rounded-full">
                  {unreadCount} new
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                onClick={onMarkAllRead}
                className="text-xs text-violet-600 hover:text-violet-800 font-600 transition-colors"
              >
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center px-4">
                <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center mb-2">
                  <svg className="w-5 h-5 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </div>
                <p className="text-sm font-600 text-foreground mb-0.5">All caught up!</p>
                <p className="text-xs text-muted-foreground">New question assignments will appear here</p>
              </div>
            ) : (
              notifications.map((n) => (
                <div
                  key={n.id}
                  className={`px-4 py-3 border-b border-border last:border-0 transition-colors ${
                    !n.read ? 'bg-violet-50/50' : 'hover:bg-muted/40'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {!n.read && (
                      <div className="w-2 h-2 bg-violet-500 rounded-full mt-1.5 flex-shrink-0" />
                    )}
                    <div className={`flex-1 min-w-0 ${n.read ? 'pl-5' : ''}`}>
                      <div className="flex items-center justify-between gap-1 mb-0.5">
                        <p className="text-xs font-600 text-foreground truncate">{n.seniorName}</p>
                        <span className="text-xs text-muted-foreground flex-shrink-0">{timeAgo(n.receivedAt)}</span>
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-2 mb-1.5 leading-relaxed">
                        {n.questionType === 'audio' ? '🎙 Audio question' : n.questionText}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full font-500">
                          {n.specialisation}
                        </span>
                        <button
                          onClick={() => {
                            onViewQuestion(n.questionId);
                            onDismiss(n.id);
                            onToggle();
                          }}
                          className="text-xs font-600 text-violet-600 hover:text-violet-800 transition-colors"
                        >
                          Answer →
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
